// CategoryComparisonChart.tsx
// Última auditoría vs anterior — scorecard con selectores de negocio y formulario
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { CategoryComparison } from '../../services/analyticsService';

interface CategoryComparisonChartProps {
  data: CategoryComparison[];
  title?: string;
}

// ── Color semáforo ──────────────────────────────────────────────────────────
const scoreColor = (score: number | null): string => {
  if (score === null) return '#BDBDBD';
  if (score >= 80) return '#43A047'; // verde
  if (score >= 60) return '#FFA000'; // ámbar
  return '#E53935';                  // rojo
};

// ── Badge de delta ──────────────────────────────────────────────────────────
const DeltaBadge: React.FC<{ delta: number | null }> = ({ delta }) => {
  if (delta === null) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: '0.7rem' }}>
        Primera auditoría
      </Typography>
    );
  }
  const positive = delta > 0.5;
  const negative = delta < -0.5;
  const arrow = positive ? '↑' : negative ? '↓' : '→';
  const color = positive ? '#43A047' : negative ? '#E53935' : '#757575';
  const label = delta > 0 ? `+${delta.toFixed(1)}%` : delta === 0 ? '0%' : `${delta.toFixed(1)}%`;
  return (
    <Typography variant="caption" fontWeight={700} sx={{ color, flexShrink: 0, fontSize: '0.72rem' }}>
      {arrow} {label}
    </Typography>
  );
};

// ── Tipos internos ───────────────────────────────────────────────────────────
interface RowData {
  key: string;
  category_name: string;
  form_name: string;
  form_id: number;
  business_id: number;
  business_name: string;
  last_score: number | null;
  previous_score: number | null;
  delta: number | null;
}

// ── Componente principal ─────────────────────────────────────────────────────
const CategoryComparisonChart: React.FC<CategoryComparisonChartProps> = ({
  data,
  title = 'Última auditoría vs anterior',
}) => {
  // ── Listas únicas de negocios y formularios ──────────────────────────────
  const businesses = useMemo(() => {
    if (!data || data.length === 0) return [];
    const map = new Map<number, string>();
    data.forEach((d) => map.set(d.business_id, d.business_name));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const [selectedBiz, setSelectedBiz] = useState<number | ''>('');
  const [selectedForm, setSelectedForm] = useState<number | ''>('');

  const activeBiz: number | null = selectedBiz !== '' ? selectedBiz : businesses[0]?.id ?? null;

  const formsForBiz = useMemo(() => {
    if (!data || activeBiz === null) return [];
    const map = new Map<number, string>();
    data
      .filter((d) => d.business_id === activeBiz)
      .forEach((d) => map.set(d.form_id, d.form_name));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, activeBiz]);

  const activeForm: number | null = selectedForm !== '' ? selectedForm : formsForBiz[0]?.id ?? null;

  const rows = useMemo<RowData[]>(() => {
    if (!data || data.length === 0 || activeBiz === null || activeForm === null) return [];
    return data
      .filter((d) => d.business_id === activeBiz && d.form_id === activeForm)
      .map((d) => ({
        key: `${d.business_id}_${d.form_id}_${d.category_name.trim().toLowerCase()}`,
        category_name: d.category_name,
        form_name: d.form_name,
        form_id: d.form_id,
        business_id: d.business_id,
        business_name: d.business_name,
        last_score: d.last_score,
        previous_score: d.previous_score,
        delta:
          d.last_score !== null && d.previous_score !== null
            ? parseFloat((d.last_score - d.previous_score).toFixed(1))
            : null,
      }))
      // Peor primero (áreas críticas arriba); sin puntaje al final
      .sort((a, b) => (a.last_score ?? 101) - (b.last_score ?? 101));
  }, [data, activeBiz, activeForm]);

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 260, gap: 1 }}>
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          Sin datos de comparación
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={340}>
          Se necesitan al menos dos auditorías completadas para mostrar la comparación.
        </Typography>
      </Box>
    );
  }

  const multipleBusinesses = businesses.length > 1;
  const multipleForms = formsForBiz.length > 1;

  return (
    <Box>
      {title && (
        <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="text.primary">
          {title}
        </Typography>
      )}

      {/* Selectores */}
      {(multipleBusinesses || multipleForms) && (
        <Stack direction="row" gap={2} mb={2} flexWrap="wrap">
          {multipleBusinesses && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Negocio</InputLabel>
              <Select
                value={selectedBiz !== '' ? selectedBiz : (businesses[0]?.id ?? '')}
                label="Negocio"
                onChange={(e: SelectChangeEvent<number | ''>) => {
                  setSelectedBiz(e.target.value as number | '');
                  setSelectedForm(''); // reset form when biz changes
                }}
              >
                {businesses.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {multipleForms && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Tipo de auditoría</InputLabel>
              <Select
                value={selectedForm !== '' ? selectedForm : (formsForBiz[0]?.id ?? '')}
                label="Tipo de auditoría"
                onChange={(e: SelectChangeEvent<number | ''>) =>
                  setSelectedForm(e.target.value as number | '')
                }
              >
                {formsForBiz.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      )}

      {/* Leyenda */}
      <Stack direction="row" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Box sx={{ width: 28, height: 7, borderRadius: 3, bgcolor: '#616161' }} />
          <Typography variant="caption" color="text.secondary">Última</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Box sx={{ width: 28, height: 5, borderRadius: 3, bgcolor: '#616161', opacity: 0.35 }} />
          <Typography variant="caption" color="text.secondary">Anterior</Typography>
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
          Semáforo: <Box component="span" sx={{ color: '#E53935', fontWeight: 700 }}>&lt;60%</Box>
          {' · '}
          <Box component="span" sx={{ color: '#FFA000', fontWeight: 700 }}>60–80%</Box>
          {' · '}
          <Box component="span" sx={{ color: '#43A047', fontWeight: 700 }}>≥80%</Box>
        </Typography>
      </Stack>

      {/* Filas de categorías */}
      <Stack gap={2.5}>
        {rows.map((row) => {
          const score = row.last_score;
          const prevScore = row.previous_score;
          const pct = score ?? 0;
          const prevPct = prevScore ?? 0;
          return (
            <Box key={row.key}>
              {/* Encabezado: nombre + puntaje + delta */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="text.primary"
                  sx={{ minWidth: 0, flex: 1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}
                >
                  {row.category_name}
                </Typography>

                <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexShrink: 0, ml: 2 }}>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{ color: scoreColor(score), minWidth: 46, textAlign: 'right', fontSize: '0.88rem' }}
                  >
                    {score !== null ? `${score.toFixed(1)}%` : '—'}
                  </Typography>
                  <DeltaBadge delta={row.delta} />
                </Stack>
              </Stack>

              {/* Barra "Última" — más gruesa */}
              <Box sx={{ position: 'relative', mb: 0.5 }}>
                <Box sx={{ height: 11, borderRadius: 5, bgcolor: '#EEEEEE', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${pct}%`,
                      height: '100%',
                      borderRadius: 5,
                      bgcolor: scoreColor(score),
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
              </Box>

              {/* Barra "Anterior" — más delgada + opaca */}
              {prevScore !== null && (
                <Box sx={{ position: 'relative', mb: 0.5 }}>
                  <Box sx={{ height: 7, borderRadius: 5, bgcolor: '#EEEEEE', overflow: 'hidden' }}>
                    <Box
                      sx={{
                        width: `${prevPct}%`,
                        height: '100%',
                        borderRadius: 5,
                        bgcolor: scoreColor(prevScore),
                        opacity: 0.55,
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Marcas de referencia 60 / 80 */}
              <Box sx={{ position: 'relative', height: 14 }}>
                {[
                  { val: 60, color: '#FFA000' },
                  { val: 80, color: '#43A047' },
                ].map(({ val, color }) => (
                  <React.Fragment key={val}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${val}%`,
                        top: 0,
                        borderLeft: `1px dashed ${color}`,
                        height: 8,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ position: 'absolute', left: `calc(${val}% + 3px)`, top: 0, fontSize: '0.6rem', lineHeight: 1 }}
                    >
                      {val}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CategoryComparisonChart;
